import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Sheet } from '../ui/Sheet'
import { Icon } from '../ui/Icon'
import { onAgentShare } from '../servers/commandStream'
import { getSharedTabShot, getSharedTabs, shareErrText, type IncomingAgentShare } from '../transport/relay'
import { TabHandle, useTabTuck } from '../shell/tabTuck'

/** The corner's picture, and the sheet's — the widths your own agent's corner asks for. */
const SMALL = 320
const LARGE = 1200

/**
 * ITS CHROME TABS, FOR A FRIEND — owner, 2026-10-17: 可以在分享選單裡面選擇是否要把Attach到該agent的
 * Chrome Tab … 的螢幕截圖也讓好友可以看到. Your own agent's tab corner (shell/AgentTabCorner), in the same
 * place and look, fed by the share: only the tabs the shared agent holds, a picture of one at a time,
 * no address, nothing that changes the tab. A picture is taken when the corner first shows, when the
 * agent starts or ends a turn, and when the reader opens it — never on a clock. Swiped to the right it is put away
 * behind a handle, as your own agent's is (shell/tabTuck.tsx), and pictures nothing while away.
 */
export function SharedTabCorner({ share }: { share: IncomingAgentShare }) {
  const id = share.share_id
  const qc = useQueryClient()
  const tuck = useTabTuck()
  const tabs = useQuery({ queryKey: ['shared-tabs', id], queryFn: () => getSharedTabs(id), staleTime: 30_000 })
  const held = tabs.data?.tabs ?? []
  const first = held[0]
  const [open, setOpen] = useState<string | null>(null)
  const small = useQuery({
    queryKey: ['shared-tab-shot', id, first?.key ?? '', SMALL],
    queryFn: () => getSharedTabShot(id, first!.key, SMALL),
    enabled: !!first?.shots && !tuck.away,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  })
  // A turn started or ended: the tabs and the picture may have moved with it.
  useEffect(
    () =>
      onAgentShare((sid, what) => {
        if (sid !== id || what !== 'activity') return
        void qc.invalidateQueries({ queryKey: ['shared-tabs', id] })
        void qc.invalidateQueries({ queryKey: ['shared-tab-shot', id] })
      }),
    [id, qc],
  )
  if (!first) return null
  const image = small.data?.image
  const others = held.length - 1
  return (
    <>
      {tuck.away && <TabHandle label={`Browser tab: ${first.title || 'its tab'}`} onOut={tuck.bringBack} />}
      {!tuck.away && (
        <button
          type="button"
          className={tuck.back ? 'tab-corner back' : 'tab-corner'}
          {...tuck.swipe}
          onClick={() => {
            if (tuck.tapped()) setOpen(first.key)
          }}
          aria-haspopup="dialog"
          aria-label={`Open ${first.title || 'its tab'}${others > 0 ? `, ${others} more tab${others === 1 ? '' : 's'}` : ''}`}
          title={first.title}
        >
          {first.shots && (
            <span className="tab-corner-pic">
              {image ? <img src={`data:${image.mime};base64,${image.base64}`} alt="" /> : <Icon name="tab" size={18} />}
            </span>
          )}
          <span className="tab-corner-name">
            <span className="art-dot art-ok" aria-hidden="true" />
            <span className="tab-corner-title">{first.title || 'Tab'}</span>
            {others > 0 && <span className="tab-corner-more">+{others}</span>}
          </span>
        </button>
      )}
      {open && (
        <SharedTabSheet id={id} tabs={held} picked={open} onPick={setOpen} onClose={() => setOpen(null)} />
      )}
    </>
  )
}

function SharedTabSheet({
  id,
  tabs,
  picked,
  onPick,
  onClose,
}: {
  id: string
  tabs: { key: string; title: string; shots: boolean }[]
  picked: string
  onPick: (key: string) => void
  onClose: () => void
}) {
  const tab = tabs.find((t) => t.key === picked) ?? tabs[0]!
  const big = useQuery({
    queryKey: ['shared-tab-shot', id, tab.key, LARGE],
    queryFn: () => getSharedTabShot(id, tab.key, LARGE),
    enabled: tab.shots,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const image = big.data?.image
  return (
    <Sheet title={tab.title || 'Tab'} className="sheet-tall" onClose={onClose}>
      {tabs.length > 1 && (
        <div className="sts-tabs" role="group" aria-label="Its tabs">
          {tabs.map((t) => (
            <button key={t.key} type="button" aria-pressed={t.key === tab.key} onClick={() => onPick(t.key)}>
              {t.title || 'Tab'}
            </button>
          ))}
        </div>
      )}
      {!tab.shots && <p className="pm-empty">Their Chrome extension needs an update to show its tabs.</p>}
      {big.isPending && tab.shots && <p className="pm-empty">Taking a picture…</p>}
      {big.isError && <p className="pm-empty sb-err">{shareErrText(big.error)}</p>}
      {image && <img className="sts-img" src={`data:${image.mime};base64,${image.base64}`} alt={tab.title} />}
      {tab.shots && (
        <button type="button" className="sheet-btn sts-again" disabled={big.isFetching} onClick={() => void big.refetch()}>
          {big.isFetching ? 'Taking a picture…' : 'Take a new picture'}
        </button>
      )}
    </Sheet>
  )
}
